import { useState } from 'react';
import { adminViews } from '@cwos/api-client';
import { ExportCsvButton } from './ExportCsvButton.js';
import {
  EXPORT_CATALOG,
  countDataRows,
  isDateOnly,
  isValidRange,
  sinceIso,
  untilIso,
  type ExportDescriptor,
  type ExportKind,
} from './exportsCatalog.js';

/**
 * The Exports area (P0-14). One reachable place that lists every export the
 * product offers, what each contains, and — where it applies — a date range to
 * narrow it. Every card's button is the existing `ExportCsvButton`: it already
 * renders nothing for a principal without `reporting:export` (default-deny),
 * shows an honest busy label while a download runs, surfaces a failure inline,
 * and says out loud when the server capped the row count.
 *
 * What this screen adds ON TOP of that button: the catalog itself (so an
 * export exists to be found at all), date-range inputs for the exports that
 * accept one, and a per-card "last export" line that turns a silent
 * header-only CSV into a stated empty result rather than a file nobody
 * understands.
 *
 * Route-gated on `reporting:export` at registration (see App.tsx) — this
 * screen has no purpose for a principal who cannot export.
 */
export function ExportsScreen() {
  return (
    <div>
      <h1 className="rn-card-title" style={{ marginBottom: 'var(--space-8)' }}>
        Exports
      </h1>
      <p className="rn-muted" style={{ marginTop: 0, marginBottom: 'var(--space-16)' }}>
        Download these as CSV. Every file is scoped to your organization only, and every download is
        written to the audit log.
      </p>
      <div style={{ display: 'grid', gap: 'var(--space-16)' }}>
        {EXPORT_CATALOG.map((d) => (
          <ExportCard key={d.key} descriptor={d} />
        ))}
      </div>
    </div>
  );
}

function runnerFor(
  kind: ExportKind,
  since: string,
  until: string,
): () => Promise<adminViews.CsvExport> {
  const sinceOk = isDateOnly(since) ? since : undefined;
  const untilOk = isDateOnly(until) ? until : undefined;
  switch (kind) {
    case 'workforce':
      return () => adminViews.exportWorkforceCsv();
    case 'exceptions':
      return () =>
        adminViews.exportExceptionsCsv({
          since: sinceOk ? sinceIso(sinceOk) : undefined,
          until: untilOk ? untilIso(untilOk) : undefined,
        });
    case 'verification':
      return () =>
        adminViews.exportVerificationCsv({
          since: sinceOk ? sinceIso(sinceOk) : undefined,
          until: untilOk ? untilIso(untilOk) : undefined,
        });
    case 'payroll':
      return () => adminViews.exportPayrollCsv({ since: sinceOk, until: untilOk });
  }
}

interface LastExport {
  rows: number;
  truncated: boolean;
}

function ExportCard({ descriptor }: { descriptor: ExportDescriptor }) {
  const [since, setSince] = useState('');
  const [until, setUntil] = useState('');
  const [last, setLast] = useState<LastExport | null>(null);
  const rangeOk = isValidRange(since, until);

  /**
   * THE CLAIM FOLLOWS THE THING IT CLAIMS.
   *
   * `setLast(...)` used to run here, on the line after the fetch resolved and
   * BEFORE `ExportCsvButton` called `downloadTextFile`. The server confirming
   * the rows is not the same event as the operator getting a file, and this
   * line is about the file: "Last export: 412 rows" is a sentence about
   * something in their Downloads folder. A browser-side download failure
   * therefore rendered a contradiction — "Couldn't export: …" and "Last
   * export: 412 rows" side by side in the same card — and the row count is the
   * more confident-looking of the two, so an operator goes looking for a file
   * that was never written and concludes their machine ate it.
   *
   * So this half only clears the previous claim, and the new one is made in
   * `onDownloaded`, which fires after the download returns.
   */
  const onExport = async (): Promise<adminViews.CsvExport> => {
    setLast(null);
    return runnerFor(descriptor.key, since, until)();
  };

  return (
    <section className="rn-card">
      <h2 className="rn-card-title">{descriptor.title}</h2>
      <p className="rn-muted" style={{ marginTop: 'var(--space-6)' }}>
        {descriptor.description}
      </p>

      {descriptor.dateRange ? (
        <div
          style={{
            display: 'flex',
            gap: 'var(--space-10)',
            flexWrap: 'wrap',
            alignItems: 'end',
            marginTop: 'var(--space-12)',
          }}
        >
          <label style={{ display: 'grid', gap: 'var(--space-6)' }}>
            <span className="rn-muted">From (optional)</span>
            <input
              style={{ marginBottom: 0 }}
              type="date"
              aria-label={`${descriptor.title} — from`}
              value={since}
              onChange={(e) => setSince(e.target.value)}
            />
          </label>
          <label style={{ display: 'grid', gap: 'var(--space-6)' }}>
            <span className="rn-muted">To (optional)</span>
            <input
              style={{ marginBottom: 0 }}
              type="date"
              aria-label={`${descriptor.title} — to`}
              value={until}
              onChange={(e) => setUntil(e.target.value)}
            />
          </label>
        </div>
      ) : null}

      {descriptor.dateRange && !rangeOk ? (
        <p className="rn-muted" role="alert" style={{ marginTop: 'var(--space-8)' }}>
          The “to” date can’t be before the “from” date.
        </p>
      ) : null}

      <div style={{ marginTop: 'var(--space-12)' }}>
        {!descriptor.dateRange || rangeOk ? (
          <ExportCsvButton
            fallbackFilename={descriptor.fallbackFilename}
            onExport={onExport}
            onDownloaded={(result) =>
              setLast({ rows: countDataRows(result.text), truncated: result.truncated })
            }
          />
        ) : null}
      </div>

      {last ? (
        <p className="rn-muted" role="status" style={{ marginTop: 'var(--space-8)' }}>
          {last.rows === 0
            ? 'Last export: nothing matched — an empty file (header row only) downloaded.'
            : `Last export: ${last.rows} row${last.rows === 1 ? '' : 's'}${
                last.truncated ? ' (capped — narrow the range for a complete export)' : ''
              }.`}
        </p>
      ) : null}
    </section>
  );
}
